import CommissionInput from "@/components/shared/CommissionInput/CommissionInput";
import Divider from "@/components/shared/Divider/Divider";
import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import { Button } from "@/components/ui/button";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCoproducers } from "@/pages/EditProduct/Coproductor/hooks/useCoProducer.ts";
import { CoProducer } from "@/services/coproducer.service.ts";
import { formatDate } from "@/utils/dateHelpers";
import { Info } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface EditCoProducerSheetProps {
  coProducer: CoProducer;
  isOpen: boolean;
  onClose: () => void;
}

export default function SideQuickEdit({
  coProducer,
  isOpen,
  onClose,
}: EditCoProducerSheetProps) {
  const { t } = useTranslation("coproducer");
  const { updateCoProducer, isPending } = useCoproducers();

  const commissionValue = coProducer.invite.percentage ?? coProducer.invite.commission_value ?? "0";

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      email: coProducer.email,
      percentage: commissionValue,
      participate_producer_sales: true,
      participate_affiliates_sales: false,
    },
  });

  const onSubmit = async (data: any) => {
    const formatedData = {
      ...data,
      percentage: parseFloat(data.percentage).toFixed(2).toString(),
    };
    updateCoProducer({ productInviteId: coProducer.id, data: formatedData });
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        email: coProducer.email,
        percentage: coProducer.invite.percentage ?? coProducer.invite.commission_value ?? "0",
        participate_producer_sales: true,
        participate_affiliates_sales: false,
      });
    }
  }, [coProducer, isOpen, reset]);

  return (
    <section className="sm:overflow-y-hidden flex flex-col justify-between h-full">
      <SheetHeader className="p-6">
        <SheetTitle className="flex gap-2"> {t("edit_title")}</SheetTitle>
      </SheetHeader>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex flex-col gap-4 px-6">
          {coProducer?.invite?.user ? (
            <div className="flex flex-col gap-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted">{t("name")}:</span>
                <span className="text-sm">{coProducer.invite?.user?.name}</span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-sm text-muted">{t("email")}:</span>
                <span className="text-sm">{coProducer.email}</span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-sm text-muted">{t("join_date")}:</span>
                <span className="text-sm font-semibold">
                  {formatDate(coProducer.invite?.user?.created_at)}
                </span>
              </div>
            </div>
          ) : (
            <Alert className="h-auto" variant="info">
              <Info className="h-[17px] w-[17px]" />
              <div className="flex flex-col gap-1">
                <span className="text-white text-sm">{coProducer.email}</span>
                <AlertDescription>{t("alert_description")}</AlertDescription>
              </div>
            </Alert>
          )}

          <Divider />

          <div className="w-[50%]">
            <CommissionInput
              type="text"
              label={t("commission")}
              defaultValue={Number(coProducer.invite.percentage ?? coProducer.invite.commission_value ?? 0)}
              placeholder="1.00"
              maxLength={5}
              dataCy="input-edit-coproducer-commission"
              {...register("percentage", {
                required: t("commission_required"),
                pattern: {
                  value: /^\d+\.?\d{0,2}$/,
                  message: t("commission_invalid_format"),
                },
                validate: {
                  validRange: (value) => {
                    const numValue = parseFloat(value);
                    return (
                      (numValue >= 0.01 && numValue <= 99) ||
                      t("commission_range_error")
                    );
                  },
                },
              })}
              onChangeCommission={(value) => setValue("percentage", value)}
              helperText={errors.percentage?.message}
            />
          </div>
        </section>

        <section className="flex border-t items-center justify-end gap-4 px-6 py-4 w-full max-sm:pb-12">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            data-cy="btn-submit-edit-coproducer"
          >
            {isPending ? t("saving") : t("save_changes")}
          </Button>
        </section>
      </form>
    </section>
  );
}
